/**
 * AppConfig — composed from the per-slot modules in this directory.
 *
 * Layer-driven slots (`table.tabs`) are derived from
 * the table in `layerConfig.ts` — add layers there, not here. The legend is
 * a single tab mounting `LayerList`, which itself walks the table, so it
 * never changes when a layer is added either.
 */

import type { AppConfig } from '#lib/apps/types.js';
import Logo from '#lib/assets/logos/abley.svg?raw';
import IconModuleLayerList from '#lib/assets/modules/layer-list.svg?raw';
import IconModuleLegend from '#lib/assets/modules/legend.svg?raw';

import { tableTabs, moduleTable, type INswRlgspSystemLayers } from './layerConfig';
import LayerList from './legends/LayerList.svelte';
import ActiveLegend from '#lib/components/map/legend/ActiveLegend.svelte';
import { sectionsFromModuleTable } from '#lib/components/map/legend/sectionsFromModuleTable.js';
import type { LegendSection } from '#lib/components/map/legend/types.js';
import { NSW_RLGSP_DATASET } from './layers';
import { widgets } from './dashboard';
import { menus } from './header';
import { loadSystem } from './loaders';
import { parseLegendLayers } from './legendHelpers';
import { loadRlgspSprite, RLGSP_SPRITE_PATH } from './loadMapSprites';
import AttributesPanel from './AttributesPanel/AttributesPanel.svelte';
import { bboxTuple } from '#lib/common/layers.js';

const legendSections: LegendSection[] = sectionsFromModuleTable(moduleTable);

export const nswRlgspConfig: AppConfig<INswRlgspSystemLayers> = {
	id: 'nsw-rlgsp',
	name: 'NSW RLGSP',
	basePath: '/nsw-rlgsp',
	head: {
		title: 'NSW Regional LG Speed Partnership',
		description: 'NSW Regional Local Government Speed Partnership.',
		keywords: 'Speed management, Risk, LGA, NSW, TNSW'
	},
	logo: Logo,
	basemap: 'full',
	// NSW Web Imagery instead of the platform MapTiler imagery; its origin is
	// allowed in `svelte.config.js` (`connect-src` / `img-src`).
	satelliteBasemap: {
		url: 'https://portal.spatial.nsw.gov.au/aid/tile/rest/services/NSWWebImagery/MapServer',
		bounds: [140.8596, -37.5376, 159.3375, -28.085],
		attribution:
			'<a href="https://spatial.nsw.gov.au" target="_blank">© Spatial Services, NSW Department of Customer Service</a>'
	},
	version: '0.4.4',
	datasets: [NSW_RLGSP_DATASET],

	loaders: {
		system: loadSystem
	},

	// Two legend tabs — `LegendOverlay` renders one per legend entry, labelled
	// by its matching module: "Layers" toggles every layer, "Active" is a
	// read-only key of just the layers currently switched on.
	modules: [
		{ id: 'Layers', label: 'Layers', icon: IconModuleLayerList },
		{ id: 'Legend', label: 'Legend', icon: IconModuleLegend }
	],
	legends: [
		{ moduleId: 'Layers', component: LayerList },
		{ moduleId: 'Legend', component: ActiveLegend }
	],

	parseLegendLayers,
	legendSections,
	legendSpritePath: RLGSP_SPRITE_PATH,

	map: {
		onLoad: loadRlgspSprite,
		// New South Wales, Australia — shown before crash data loads.
		defaultBounds: [140.9, -37.6, 153.7, -28.1],
		getDataBounds: (systemData) => {
			const bbox = systemData?.['Road Crash Data']?.['FSI Crashes - All']?.bbox;
			return bbox && bboxTuple(bbox);
		},
		measureUnits: 'metric'
	},

	table: {
		tabs: tableTabs,
		defaultTabId: tableTabs[0]?.id ?? '',
		filterPlaceholder: 'Filter table by road/region',
		// Too many layer-driven tabs to fit across the top.
		tabPlacement: 'left'
	},

	filters: [],

	dashboard: {
		widgets,
		backgroundImage: '/landing_bg.avif'
	},

	header: {
		menus,
		// TNSW keys can't add GIS Services, so a disabled entry would only confuse.
		hideUnavailableTools: true
	},

	attributes: {
		panel: AttributesPanel,
		countermeasures: null
	}
};
